import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  Chip,
  CardActionArea,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { BlogPost, formatDate } from "../utils/blogUtils";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

interface BlogCardProps {
  post: BlogPost;
  featured?: boolean;
}

const DEFAULT_IMAGE =
  "https://images.unsplash.com/photo-1586281380117-5a60ae2050cc?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1200&q=80";

const BlogCard: React.FC<BlogCardProps> = ({ post, featured = false }) => {
  const [imageError, setImageError] = useState(false);
  const [hovered, setHovered] = useState(false);

  const handleImageError = () => {
    console.error(`Failed to load image: ${post.coverImage}`);
    setImageError(true);
  };

  return (
    <Card
      elevation={1}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        borderRadius: 3,
        overflow: "hidden",
        transition: "transform 0.3s ease, box-shadow 0.3s ease",
        "&:hover": {
          transform: "translateY(-8px)",
          boxShadow: 4,
        },
        position: "relative",
        cursor: "pointer",
      }}
      component={RouterLink}
      to={`/blog/${post.slug}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <Box sx={{ position: "relative" }}>
        <CardMedia
          component="img"
          image={imageError ? DEFAULT_IMAGE : post.coverImage || DEFAULT_IMAGE}
          alt={post.title}
          onError={handleImageError}
          sx={{
            height: 200,
            objectFit: "cover",
            transition: "transform 0.3s ease",
            transform: hovered ? "scale(1.05)" : "scale(1)",
          }}
        />
        <Chip
          label={formatDate(post.date)}
          size="small"
          sx={{
            position: "absolute",
            bottom: -12,
            left: 16,
            backgroundColor: "white",
            color: "text.secondary",
            fontWeight: 500,
            boxShadow: "0px 2px 4px rgba(0,0,0,0.1)",
            fontSize: "0.7rem",
            zIndex: 2,
          }}
        />
      </Box>
      <CardContent
        sx={{
          flex: "1 0 auto",
          display: "flex",
          flexDirection: "column",
          pt: 3,
          pb: 2,
          px: 3,
          height: "100%",
        }}
      >
        <Typography
          variant="h6"
          component="h3"
          gutterBottom
          sx={{
            fontWeight: 600,
            fontSize: "1.1rem",
            lineHeight: 1.4,
            mb: 1.5,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            height: "2.8rem",
            color: hovered ? "primary.main" : "inherit",
            transition: "color 0.3s ease",
          }}
        >
          {post.title}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            mb: 2,
            height: "4.5rem",
          }}
        >
          {post.excerpt}
        </Typography>
        <Box sx={{ mt: "auto", display: "flex", alignItems: "center" }}>
          <Typography
            variant="button"
            sx={{
              color: "primary.main",
              textTransform: "none",
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              "& .arrow": {
                transition: "transform 0.3s ease",
                transform: hovered ? "translateX(4px)" : "translateX(0)",
                ml: 0.5,
              },
            }}
          >
            Read Article
            <ArrowForwardIcon className="arrow" fontSize="small" />
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default BlogCard;
